import * as React from "react"
import { Spacer } from "gatsby-interface"
import { HostingDomain, HostingCertificate } from "@modules/graphql/types"
import { DomainDetails } from "./DomainDetails"
import { DnsSetupInstruction } from "./DomainDetails.elements"
import { useValidateCloudCdnDomain } from "../hooks/useValidateCloudCdnDomain"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useTracker, SegmentEventType } from "@modules/analytics"

export type PrimarySecondaryComboProps = {
  primaryDomain: HostingDomain
  primaryCertificate?: HostingCertificate
  secondaryDomain?: HostingDomain
  secondaryCertificate?: HostingCertificate
  primaryDomainOptions?: HostingDomain[]
  siteId: string
  onRemove: (id: string) => void
  onMakePrimary: (id: string) => void
  onRequestCertificate: (id: string) => void
  allowToEdit?: boolean
}

export function PrimarySecondaryCombo({
  siteId,
  primaryDomain,
  primaryCertificate,
  secondaryDomain,
  secondaryCertificate,
  onRemove,
  onMakePrimary,
  primaryDomainOptions,
  onRequestCertificate,
  allowToEdit,
}: PrimarySecondaryComboProps) {
  const { trackSegment, trackAction } = useTracker()
  const [validateCloudCdnDomain] = useValidateCloudCdnDomain(siteId)
  const [validatingPrimary, setValidatingPrimary] = React.useState<boolean>(
    false
  )
  const [validatingSecondary, setValidatingSecondary] = React.useState<boolean>(
    false
  )
  const [setError, errorAlert] = useTriggerErrorAlert()

  const handleDomainValidation = () => {
    if (!primaryDomain.verified) {
      setValidatingPrimary(true)

      validateCloudCdnDomain(primaryDomain.id)
        .then(() => {
          setValidatingPrimary(false)
        })
        .catch(err => setError(err))
    }

    if (secondaryDomain && !secondaryDomain.verified) {
      setValidatingSecondary(true)

      validateCloudCdnDomain(secondaryDomain.id)
        .then(() => {
          setValidatingSecondary(false)
        })
        .catch(err => setError(err))
    }

    trackAction({
      eventType: `TRACK_EVENT`,
      name: `Domain status checked`,
      uiSource: `Site Settings`,
    })

    trackSegment({
      type: SegmentEventType.Track,
      event: "Domain status checked",
      properties: {
        location: "Site Settings",
      },
    })
  }

  const isError = primaryDomain.errorMessage || secondaryDomain?.errorMessage
  const isVerified = secondaryDomain
    ? primaryDomain.verified && secondaryDomain?.verified
    : primaryDomain.verified
  const isDefault = primaryDomain.cloudCdnDefault

  return (
    <React.Fragment>
      <DomainDetails
        domain={primaryDomain}
        siteId={siteId}
        onRemove={() => onRemove(primaryDomain.id)}
        primaryDomainOptions={primaryDomainOptions}
        onRequestCertificate={() => onRequestCertificate(primaryDomain.id)}
        certificate={primaryCertificate}
        hasSecondary={!!secondaryDomain}
        validating={validatingPrimary}
        allowToEdit={allowToEdit}
      />

      {secondaryDomain && (
        <DomainDetails
          domain={secondaryDomain}
          siteId={siteId}
          onRemove={() => onRemove(secondaryDomain.id)}
          onMakePrimary={() => onMakePrimary(secondaryDomain.id)}
          onRequestCertificate={() => onRequestCertificate(secondaryDomain.id)}
          certificate={secondaryCertificate}
          validating={validatingSecondary}
          allowToEdit={allowToEdit}
        />
      )}

      {!isError && !isDefault && !isVerified && (
        <DnsSetupInstruction
          primaryDomain={primaryDomain}
          secondaryDomain={secondaryDomain}
          validating={validatingPrimary || validatingSecondary}
          onValidate={handleDomainValidation}
          allowToEdit={allowToEdit}
        />
      )}

      {errorAlert && (
        <React.Fragment>
          <Spacer size={4} />
          {errorAlert}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
